import axios from 'axios';
import { API_CONFIG, API_ENDPOINTS, ADMIN_KEY } from '../config/api';
import { Agent, QueryRequest, QueryResponse, UsageStats, TokenUsageSummary, TokenUsageHistory, SettingsResponse, DeploymentClient, GeneratedCode } from '../types';

const coreApi = axios.create({
  baseURL: API_CONFIG.core,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Admin API instance — includes superadmin key for protected endpoints
const adminApi = axios.create({
  baseURL: API_CONFIG.core,
  headers: {
    'Content-Type': 'application/json',
    'x-superadmin-key': ADMIN_KEY,
  },
});

const billingApi = axios.create({
  baseURL: API_CONFIG.billing,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Agent Services
export const agentService = {
  getAll: async (role: string = 'lawyer'): Promise<Agent[]> => {
    // Use 'lawyer' role to get all agents including JUNIOR
    const { data } = await coreApi.get(`${API_ENDPOINTS.agents}?role=${role}`);
    return data.agents;
  },

  getById: async (id: string): Promise<Agent> => {
    const { data } = await coreApi.get(API_ENDPOINTS.agentById(id));
    return data.agent;
  },

  create: async (agentData: Partial<Agent>): Promise<Agent> => {
    const { data } = await adminApi.post(API_ENDPOINTS.admin.agents, agentData);
    return data.agent;
  },

  update: async (id: string, agentData: Partial<Agent>): Promise<Agent> => {
    const { data } = await adminApi.patch(API_ENDPOINTS.admin.agentById(id), agentData);
    return data.agent;
  },

  delete: async (id: string): Promise<void> => {
    await adminApi.delete(API_ENDPOINTS.admin.agentById(id));
  },
};

// Query Services
export const queryService = {
  sendQuery: async (queryData: QueryRequest): Promise<QueryResponse> => {
    const { data } = await coreApi.post(API_ENDPOINTS.query, queryData);
    return data;
  },

  sendQueryStream: async (
    queryData: QueryRequest,
    onChunk: (text: string) => void,
    onStatus?: (status: string) => void,
    onDone?: (metadata: any) => void,
    onError?: (error: string) => void,
  ): Promise<void> => {
    const response = await fetch(`${API_CONFIG.core}/api/query/stream`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(queryData),
    });

    if (!response.ok || !response.body) {
      const text = await response.text();
      throw new Error(text || `Request failed with status ${response.status}`);
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });

      const lines = buffer.split('\n');
      buffer = lines.pop() || '';

      let currentEvent = '';
      for (const line of lines) {
        if (line.startsWith('event: ')) {
          currentEvent = line.slice(7).trim();
        } else if (line.startsWith('data: ')) {
          try {
            const data = JSON.parse(line.slice(6));
            if (currentEvent === 'chunk' && data.text) {
              onChunk(data.text);
            } else if (currentEvent === 'status' && onStatus) {
              onStatus(data.message || data.step || '');
            } else if (currentEvent === 'done' && onDone) {
              onDone(data);
            } else if (currentEvent === 'error' && onError) {
              onError(data.error || 'Unknown error');
            }
          } catch (e) {
            // ignore parse errors
          }
        }
      }
    }
  },

  // Pro Query - Non-streaming
  sendProQuery: async (queryData: QueryRequest): Promise<any> => {
    const { data } = await coreApi.post(API_ENDPOINTS.queryPro, queryData);
    return data;
  },

  // Pro Query - Streaming (SSE)
  sendProQueryStream: async (
    queryData: QueryRequest,
    onChunk: (text: string) => void,
    onStatus?: (status: string) => void,
    onRouting?: (routing: any) => void,
    onDone?: (metadata: any) => void,
    onError?: (error: string) => void,
  ): Promise<void> => {
    const response = await fetch(`${API_CONFIG.core}/api/query/pro/stream`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(queryData),
    });

    if (!response.ok || !response.body) {
      const text = await response.text();
      throw new Error(text || `Request failed with status ${response.status}`);
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });

      const lines = buffer.split('\n');
      buffer = lines.pop() || '';

      let currentEvent = '';
      for (const line of lines) {
        if (line.startsWith('event: ')) {
          currentEvent = line.slice(7).trim();
        } else if (line.startsWith('data: ')) {
          try {
            const data = JSON.parse(line.slice(6));
            if (currentEvent === 'chunk' && data.text) {
              onChunk(data.text);
            } else if (currentEvent === 'status' && onStatus) {
              onStatus(data.message || data.step || '');
            } else if (currentEvent === 'routing' && onRouting) {
              onRouting(data);
            } else if (currentEvent === 'done' && onDone) {
              onDone(data);
            } else if (currentEvent === 'error' && onError) {
              onError(data.error || 'Unknown error');
            }
          } catch (e) {
            // ignore parse errors
          }
        }
      }
    }
  },

  logQuery: async (logData: any): Promise<void> => {
    await billingApi.post(API_ENDPOINTS.logQuery, logData);
  },
};

// Usage Services
export const usageService = {
  getStats: async (userId: string): Promise<UsageStats> => {
    const { data } = await billingApi.get(`${API_ENDPOINTS.usageStats}?userId=${userId}`);
    return data;
  },
};

// Token Billing Services (from CoreAPI)
export const billingTokenService = {
  getUsageSummary: async (): Promise<TokenUsageSummary> => {
    const { data } = await coreApi.get(API_ENDPOINTS.billingUsage);
    return data.usage;
  },

  getHistory: async (limit: number = 20): Promise<TokenUsageHistory[]> => {
    const { data } = await adminApi.get(`${API_ENDPOINTS.billingHistory}?limit=${limit}`);
    return data.history;
  },
};

// Settings Services (admin-only — exposes infrastructure config)
export const settingsService = {
  getSettings: async (): Promise<SettingsResponse> => {
    const { data } = await adminApi.get(API_ENDPOINTS.settings);
    return data;
  },
};

// ── Deployment Services (admin-only) ──

export const deploymentService = {
  getClients: async (): Promise<DeploymentClient[]> => {
    const { data } = await adminApi.get('/api/deployment/clients');
    return data.clients;
  },
  getClient: async (id: string): Promise<DeploymentClient> => {
    const { data } = await adminApi.get(`/api/deployment/clients/${id}`);
    return data.client;
  },
  createClient: async (clientData: Partial<DeploymentClient>): Promise<DeploymentClient> => {
    const { data } = await adminApi.post('/api/deployment/clients', clientData);
    return data.client;
  },
  deleteClient: async (id: string): Promise<void> => {
    await adminApi.delete(`/api/deployment/clients/${id}`);
  },
  regenerateKey: async (id: string): Promise<string> => {
    const { data } = await adminApi.post(`/api/deployment/clients/${id}/regenerate-key`);
    return data.apiKey;
  },
  assignAgents: async (id: string, agentIds: string[]): Promise<DeploymentClient> => {
    const { data } = await adminApi.put(`/api/deployment/clients/${id}/agents`, { agentIds });
    return data.client;
  },
  assignApis: async (id: string, apis: string[]): Promise<DeploymentClient> => {
    const { data } = await adminApi.put(`/api/deployment/clients/${id}/apis`, { apis });
    return data.client;
  },
  setContract: async (id: string, contract: any): Promise<DeploymentClient> => {
    const { data } = await adminApi.put(`/api/deployment/clients/${id}/contract`, contract);
    return data.client;
  },
  rechargeTokens: async (id: string, amount: number): Promise<any> => {
    const { data } = await adminApi.post(`/api/deployment/clients/${id}/recharge`, { amount });
    return data.tokens;
  },
  setCaseSaving: async (id: string, enabled: boolean, pocketbaseUrl?: string): Promise<DeploymentClient> => {
    const { data } = await adminApi.put(`/api/deployment/clients/${id}/case-saving`, { enabled, pocketbaseUrl });
    return data.client;
  },
  updateBranding: async (id: string, branding: any): Promise<DeploymentClient> => {
    const { data } = await adminApi.put(`/api/deployment/clients/${id}/branding`, branding);
    return data.client;
  },
  generateCode: async (id: string, platform: string = 'all'): Promise<GeneratedCode> => {
    const { data } = await adminApi.get(`/api/deployment/clients/${id}/generate?platform=${platform}`);
    return data;
  },
  buildAndDownload: async (id: string, platform: string): Promise<{ success: boolean; buildId?: string; fileName?: string; note?: string; downloadUrl?: string; error?: string; async?: boolean }> => {
    const { data } = await adminApi.post(`/api/deployment/clients/${id}/build/${platform}`);
    return data;
  },
  launchBuild: async (buildId: string): Promise<{ success: boolean; port?: number; url?: string; error?: string }> => {
    const { data } = await adminApi.post(`/api/deployment/builds/${buildId}/launch`);
    return data;
  },
  updateAllowedOrigins: async (id: string, origins: string[]): Promise<DeploymentClient> => {
    const { data } = await adminApi.put(`/api/deployment/clients/${id}/allowed-origins`, { origins });
    return data.client;
  },
  deployToCloud: async (id: string): Promise<{ success: boolean; url?: string; port?: number; credentials?: { username: string; password: string }; message?: string; error?: string }> => {
    const { data } = await adminApi.post(`/api/deployment/clients/${id}/deploy-cloud`);
    return data;
  },
  getCloudStatus: async (id: string): Promise<{ deployed: boolean; url?: string; port?: number; status?: string; isRunning?: boolean; customDomain?: string }> => {
    const { data } = await adminApi.get(`/api/deployment/clients/${id}/cloud/status`);
    return data;
  },
  stopCloud: async (id: string): Promise<{ success: boolean; message?: string }> => {
    const { data } = await adminApi.post(`/api/deployment/clients/${id}/cloud/stop`);
    return data;
  },
  restartCloud: async (id: string): Promise<{ success: boolean; message?: string }> => {
    const { data } = await adminApi.post(`/api/deployment/clients/${id}/cloud/restart`);
    return data;
  },
  resetDevices: async (id: string): Promise<{ success: boolean; message?: string; devicesRemoved?: number }> => {
    const { data } = await adminApi.post(`/api/deployment/clients/${id}/reset-devices`);
    return data;
  },
};
